import { MenuIcon, pxToRem } from "@getbread/dough";
import { Logout as LogoutIcon, Close as CloseIcon } from "@mui/icons-material";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink } from "react-router";
import { getConfig } from "../../utils/getConfig";
import { AccountIcon } from "../icons/AccountIcon";
import { DocumentIcon } from "../icons/DocumentIcon";
import { HelpIcon } from "../icons/HelpIcon";
import { LoansIcon } from "../icons/LoansIcon";
import { useLocale } from "../LocaleProvider";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	"padding": pxToRem(16),
	"&.active, &.active svg": {
		color: theme.palette.primary.main,
	},
	"&:focus-visible": {
		outline: "none",
	},
})) as typeof MenuItem;

const HamburgerNav: React.FC = () => {
	const intl = useIntl();
	const [menuOpen, setMenuOpen] = useState<HTMLButtonElement>();
	const { locale } = useLocale();

	const handleClose = () => {
		setMenuOpen(undefined);
	};

	const faqLinks = getConfig("faqlinks").general;
	const faqLink = faqLinks[locale];

	return (
		<nav>
			<IconButton
				aria-label={
					menuOpen
						? intl.formatMessage({
								defaultMessage: "Close Navigation Menu",
							})
						: intl.formatMessage({
								defaultMessage: "Open Navigation Menu",
							})
				}
				data-testid="hamburger-nav"
				onClick={(event) => {
					setMenuOpen(event.currentTarget);
				}}
				sx={{ p: pxToRem(25), pl: pxToRem(50) }}
			>
				<MenuIcon
					style={{
						position: "absolute",
						transition: "opacity 0.3s",
						opacity: menuOpen ? 0 : 1,
						pointerEvents: menuOpen ? "none" : "auto",
					}}
				/>
				<CloseIcon
					style={{
						position: "absolute",
						transition: "opacity 0.3s",
						opacity: menuOpen ? 1 : 0,
						pointerEvents: menuOpen ? "auto" : "none",
					}}
				/>
			</IconButton>
			<Menu
				anchorEl={menuOpen}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={Boolean(menuOpen)}
				onClose={handleClose}
				slotProps={{
					paper: {
						sx: {
							borderRadius: 6,
						},
					},
				}}
			>
				<StyledMenuItem
					component={NavLink}
					to="/loans"
					onClick={handleClose}
					data-testid="mobile-loans-link"
				>
					<ListItemIcon>
						<LoansIcon />
					</ListItemIcon>
					<ListItemText>
						<FormattedMessage defaultMessage="Loans" />
					</ListItemText>
				</StyledMenuItem>
				<StyledMenuItem
					component={NavLink}
					to="/my-information"
					data-testid="mobile-account-link"
					onClick={handleClose}
				>
					<ListItemIcon>
						<AccountIcon />
					</ListItemIcon>
					<ListItemText>
						<FormattedMessage defaultMessage="My Information" />
					</ListItemText>
				</StyledMenuItem>
				<StyledMenuItem
					component={NavLink}
					to="/loan-documents"
					data-testid="mobile-loan-document-link"
					onClick={handleClose}
				>
					<ListItemIcon>
						<DocumentIcon />
					</ListItemIcon>
					<ListItemText>
						<FormattedMessage defaultMessage="Loan Documents" />
					</ListItemText>
				</StyledMenuItem>
				<StyledMenuItem
					component="a"
					href={faqLink}
					target="_blank"
					rel="noopener noreferrer"
					data-testid="mobile-faqs-link"
					onClick={handleClose}
				>
					<ListItemIcon>
						<HelpIcon />
					</ListItemIcon>
					<ListItemText>
						<FormattedMessage defaultMessage="FAQs" />
					</ListItemText>
				</StyledMenuItem>
				<StyledMenuItem
					component={NavLink}
					to="/logout"
					data-testid="mobile-sign-out-link"
					onClick={handleClose}
				>
					<ListItemIcon>
						<LogoutIcon />
					</ListItemIcon>
					<ListItemText>
						<FormattedMessage defaultMessage="Sign Out" />
					</ListItemText>
				</StyledMenuItem>
			</Menu>
		</nav>
	);
};

export { HamburgerNav };
